import React, { useCallback } from 'react';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import { useLocation, useNavigate } from 'react-router';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useTranslation } from 'react-i18next';

import UserMenu from '../../components/UserMenu/UserMenu';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';
import { modalURLFromLocation } from '../../utils/location';
import Button from '../../components/Button/Button';
import { resolvePaymentsPage } from '../../utils/paymentsPage';

const HeaderUserMenu = ({ supportsCustomerPortal = true }: { supportsCustomerPortal?: boolean }) => {
  const { t } = useTranslation('user');
  const breakpoint = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();

  const userMenuOpen = useUIStore((state) => state.userMenuOpen);
  const {
    config: { features },
    accessModel,
  } = useConfigStore((state) => ({
    config: state.config,
    accessModel: state.accessModel,
  }));
  const isLoggedIn = useAccountStore(({ user }) => !!user);

  const accountController = getModule(AccountController);
  const { paymentsEnabled, manageSubscriptionEnabled } = resolvePaymentsPage(accountController.getFeatures().paymentsPage, supportsCustomerPortal);
  const favoritesEnabled = !!features?.favoritesList;

  const canLogin = accessModel !== ACCESS_MODEL.AVOD;

  const openUserPanel = useCallback(() => useUIStore.setState({ userMenuOpen: true }), []);
  const closeUserPanel = useCallback(() => useUIStore.setState({ userMenuOpen: false }), []);

  const loginButtonClickHandler = () => navigate(modalURLFromLocation(location, 'login'));
  const signUpButtonClickHandler = () => navigate(modalURLFromLocation(location, 'create-account'));
  const logoutButtonClickHandler = () => {
    // Empty customer on a user page leads to navigate (code below), so we don't repeat it here
    accountController.logout();
  };

  const manageSubscriptionClickHandler = async () => {
    const hasSubscription = !!useAccountStore.getState().subscription;

    if (!hasSubscription) {
      return navigate(modalURLFromLocation(location, 'choose-offer'));
    }

    const customerPortalUrl = await accountController.getCustomerPortalUrl(window.location.href);

    if (customerPortalUrl) {
      window.location.href = customerPortalUrl;
    }
  };

  // disable button for mobile layout
  if (breakpoint <= Breakpoint.sm) return null;

  if (!canLogin) {
    // Fallback log out button when we don't expect the user to be signed in
    if (isLoggedIn) return <Button onClick={logoutButtonClickHandler} label={t('nav.logout')} />;

    // users can't log in or register, so we can ignore this
    return null;
  }

  return (
    <UserMenu
      open={userMenuOpen}
      onOpen={openUserPanel}
      onClose={closeUserPanel}
      isLoggedIn={isLoggedIn}
      favoritesEnabled={favoritesEnabled}
      paymentsEnabled={paymentsEnabled}
      manageSubscriptionEnabled={manageSubscriptionEnabled}
      onManageSubscriptionClick={manageSubscriptionClickHandler}
      onLoginButtonClick={loginButtonClickHandler}
      onSignUpButtonClick={signUpButtonClickHandler}
    />
  );
};

export default HeaderUserMenu;
